import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader2, AlertTriangle, Download, FlaskConical, Camera,
  CheckCircle2, XCircle, Clock, Building2, MapPin, Info
} from 'lucide-react';

const DESIGNATIONS = [20, 30, 40, 50, 60, 70, 80];
const DESIGNATION_COLORS = {
  20: 'bg-emerald-500', 30: 'bg-green-500', 40: 'bg-lime-500',
  50: 'bg-orange-400', 60: 'bg-orange-500', 70: 'bg-red-400', 80: 'bg-red-600',
};

const POSITION_EXPLANATIONS = {
  compliant_stable: 'The building is comfortably below the 60% Average Income threshold with flexibility to absorb changes.',
  compliant_constrained: 'The building is compliant but approaching the 60% limit with limited room for higher-income designations.',
  at_risk_event_driven: 'The building is near the threshold and vulnerable to disruption from vacancies, turnovers, or re-certifications.',
  at_risk_structural: 'The designation mix creates structural fragility — concentration in certain bands limits corrective options.',
  noncompliant_correctable: 'The average exceeds 60% but corrective actions (redesignation, swaps) are available to restore compliance.',
  noncompliant_critical: 'The average significantly exceeds 60% and corrective options are constrained.',
  noncompliant_irreversible: 'Structural restoration to compliance is not achievable through internal designation changes alone.',
  below_minimum_qualified: 'Fewer than 40% of units are qualified — the minimum set-aside threshold is not met.',
  needs_designation: 'Qualified units exist but have not yet been assigned income designations.',
  no_qualified_units: 'No units are currently designated as qualified low-income housing units.',
};

const FLEXIBILITY_EXPLANATIONS = {
  high: 'Significant room to absorb higher-designation units without approaching the 60% limit.',
  moderate: 'Some capacity to absorb changes, but larger moves should be evaluated carefully.',
  constrained: 'Limited flexibility — most designation changes would push the average closer to the limit.',
  minimal: 'Very little room remaining. Even small changes could breach the 60% threshold.',
  none: 'No flexibility. The average is at or above the 60% limit.',
};

const PRESSURE_EXPLANATIONS = {
  low: 'Well below the compliance threshold with minimal risk of breach.',
  moderate: 'Moderate proximity to the threshold — routine monitoring is recommended.',
  elevated: 'Approaching the threshold. Active management of designations is advised.',
  critical: 'Very close to or at the 60% limit. Immediate attention required.',
  breach: 'The average exceeds the 60% limit. The building is currently noncompliant.',
};

const API_URL = import.meta.env.VITE_API_URL || 'https://api.jpro.app/api/v1';
const BASE_URL = API_URL.replace(/\/api\/v1\/?$/, '');

function SharedSnapshot() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${BASE_URL}/shared/snapshots/${token}`);
        if (!res.ok) {
          if (res.status === 410) {
            setError('This share link has expired. Please request a new link from the property manager.');
          } else if (res.status === 404) {
            setError('Snapshot not found. The link may be invalid or the snapshot may have been removed.');
          } else {
            setError('Failed to load snapshot. Please try again later.');
          }
          setLoading(false);
          return;
        }
        setData(await res.json());
      } catch {
        setError('Unable to connect. Please check your internet connection and try again.');
      }
      setLoading(false);
    };
    load();
  }, [token]);

  const handleDownloadPdf = async () => {
    try {
      const res = await fetch(`${BASE_URL}/shared/snapshots/${token}/pdf`);
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `snapshot-${data?.snapshot?.snapshot_date || 'report'}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch { /* silently fail */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-500" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <XCircle size={48} className="text-gray-300 mx-auto mb-4" />
          <h1 className="text-lg font-medium text-gray-900 mb-2">Snapshot Unavailable</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const { snapshot, building_name, property_name, property_address, expires_at } = data;
  const compliant = snapshot.average_income != null && snapshot.average_income <= 60;
  const headroom = snapshot.average_income != null ? (60 - snapshot.average_income).toFixed(2) : null;
  const dist = snapshot.designation_distribution || {};
  const totalDesignated = Object.values(dist).reduce((s, v) => s + (Number(v) || 0), 0);
  const maxCount = Math.max(1, ...Object.values(dist).map(Number));
  const minMet = (snapshot.qualified_percentage || 0) >= 40;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">AIT Compliance Snapshot</h1>
              <div className="mt-2 space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Building2 size={14} className="text-gray-400 shrink-0" />
                  <span>{property_name} — {building_name}</span>
                </div>
                {property_address && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <MapPin size={14} className="text-gray-400 shrink-0" />
                    <span>{property_address}</span>
                  </div>
                )}
              </div>
            </div>
            <button onClick={handleDownloadPdf}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors shrink-0">
              <Download size={14} /> Download PDF
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {/* Simulation warning */}
        {snapshot.snapshot_type === 'simulation' && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-indigo-50 border border-indigo-200">
            <FlaskConical size={18} className="text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-sm font-medium text-indigo-700">Simulation Snapshot</div>
              <p className="text-xs text-indigo-600 mt-1">
                This snapshot reflects a hypothetical scenario — not the actual state of unit records.
                It was created to model the impact of potential designation or occupancy changes.
              </p>
            </div>
          </div>
        )}

        {/* Date & type badge */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Clock size={14} className="text-gray-400" />
            {new Date(snapshot.created_at).toLocaleString('en-US', {
              year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit'
            })}
          </div>
          {snapshot.snapshot_type === 'simulation' ? (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-indigo-50 text-indigo-600 border border-indigo-200">
              <FlaskConical size={11} /> Simulation
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-green-50 text-green-600 border border-green-200">
              <Camera size={11} /> Live
            </span>
          )}
        </div>

        {/* AIT overview */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-start gap-2">
            <Info size={14} className="text-gray-400 shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 leading-relaxed">
              The <strong>Average Income Test (AIT)</strong> requires that the Average Income designation across all
              qualified low-income units in a building does not exceed <strong>60% of Area Median Income (AMI)</strong>.
              A minimum of <strong>40%</strong> of units must be designated as qualified. Each qualified unit is assigned
              a designation band (20%–80% AMI) and the average of all designated units determines compliance.
            </p>
          </div>
        </div>

        {/* Compliance verdict */}
        <div className={`rounded-xl border-2 p-5 ${compliant ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
          <div className="flex items-center gap-2 mb-2">
            {compliant ? (
              <CheckCircle2 size={22} className="text-green-600" />
            ) : (
              <AlertTriangle size={22} className="text-red-600" />
            )}
            <span className={`text-lg font-bold ${compliant ? 'text-green-700' : 'text-red-700'}`}>
              {compliant ? 'Compliant' : 'Noncompliant'} — {snapshot.average_income}% Average Income
            </span>
          </div>
          <p className={`text-sm ${compliant ? 'text-green-600' : 'text-red-600'}`}>
            {headroom != null && headroom >= 0
              ? `The building is ${headroom} percentage points below the 60% maximum. This headroom provides a buffer against future designation changes.`
              : `The building exceeds the 60% maximum by ${Math.abs(headroom)} percentage points. Corrective action is required to restore compliance.`
            }
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className={`text-2xl font-bold ${compliant ? 'text-green-500' : 'text-red-500'}`}>
              {snapshot.average_income != null ? `${snapshot.average_income}%` : '—'}
            </div>
            <div className="text-xs text-gray-400 mt-1">Average Income</div>
            <p className="text-[10px] text-gray-400 mt-1">Weighted average of all designated unit bands</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-2xl font-bold text-gray-900">{snapshot.qualified_count}</div>
            <div className="text-xs text-gray-400 mt-1">
              Qualified of {snapshot.total_units} ({snapshot.qualified_percentage}%)
            </div>
            <p className={`text-[10px] mt-1 ${minMet ? 'text-green-500' : 'text-red-500'}`}>
              {minMet ? '40% minimum met' : '40% minimum NOT met'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className={`text-lg font-semibold capitalize ${
              snapshot.flexibility_index === 'high' ? 'text-green-500' :
              snapshot.flexibility_index === 'moderate' ? 'text-blue-500' :
              snapshot.flexibility_index === 'constrained' ? 'text-orange-500' : 'text-red-500'
            }`}>{snapshot.flexibility_index || '—'}</div>
            <div className="text-xs text-gray-400 mt-1">Flexibility</div>
            <p className="text-[10px] text-gray-400 mt-1">Capacity to absorb higher designations</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className={`text-lg font-semibold capitalize ${
              snapshot.pressure_level === 'low' ? 'text-green-500' :
              snapshot.pressure_level === 'moderate' ? 'text-orange-500' :
              snapshot.pressure_level === 'elevated' ? 'text-orange-500' : 'text-red-500'
            }`}>{snapshot.pressure_level || '—'}</div>
            <div className="text-xs text-gray-400 mt-1">Pressure Level</div>
            <p className="text-[10px] text-gray-400 mt-1">Proximity to the 60% threshold</p>
          </div>
        </div>

        {/* Compliance position detail */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            {compliant ? (
              <CheckCircle2 size={16} className="text-green-500" />
            ) : (
              <AlertTriangle size={16} className="text-red-500" />
            )}
            <span className={`text-sm font-semibold ${compliant ? 'text-green-700' : 'text-red-700'}`}>
              {snapshot.compliance_position?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown'}
            </span>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            {POSITION_EXPLANATIONS[snapshot.compliance_position] || 'Current compliance position could not be determined.'}
          </p>
        </div>

        {/* Flexibility & Pressure detail */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Flexibility Index</div>
            <div className={`text-sm font-semibold capitalize mb-1 ${
              snapshot.flexibility_index === 'high' ? 'text-green-600' :
              snapshot.flexibility_index === 'moderate' ? 'text-blue-600' :
              snapshot.flexibility_index === 'constrained' ? 'text-orange-600' : 'text-red-600'
            }`}>{snapshot.flexibility_index || 'N/A'}</div>
            <p className="text-xs text-gray-500 leading-relaxed">
              {FLEXIBILITY_EXPLANATIONS[snapshot.flexibility_index] || 'No data available.'}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Pressure Level</div>
            <div className={`text-sm font-semibold capitalize mb-1 ${
              snapshot.pressure_level === 'low' ? 'text-green-600' :
              snapshot.pressure_level === 'moderate' ? 'text-orange-600' :
              snapshot.pressure_level === 'elevated' ? 'text-orange-600' : 'text-red-600'
            }`}>{snapshot.pressure_level || 'N/A'}</div>
            <p className="text-xs text-gray-500 leading-relaxed">
              {PRESSURE_EXPLANATIONS[snapshot.pressure_level] || 'No data available.'}
            </p>
          </div>
        </div>

        {/* Designation distribution */}
        {Object.values(dist).some(v => v > 0) && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Designation Distribution</h3>
            <p className="text-xs text-gray-500 mb-4">
              Each qualified unit is assigned an income band (20%–80% AMI). Lower bands pull the average down, higher bands push it up.
              A balanced distribution maintains compliance flexibility. Total designated: <strong>{totalDesignated} units</strong>.
            </p>
            <div className="flex items-end gap-2 h-28">
              {DESIGNATIONS.map(d => {
                const count = dist[String(d)] || 0;
                const pct = totalDesignated > 0 ? (count / totalDesignated * 100).toFixed(0) : 0;
                return (
                  <div key={d} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-[10px] text-gray-400">{count} ({pct}%)</span>
                    <div
                      className={`w-full rounded-t ${DESIGNATION_COLORS[d]}`}
                      style={{ height: `${(count / maxCount) * 80}px`, minHeight: count > 0 ? '4px' : '0' }}
                    />
                    <span className="text-xs font-medium text-gray-600">{d}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Concentration risks */}
        {snapshot.concentration_risks?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-1">Identified Risks</h3>
            <p className="text-xs text-gray-500 mb-3">
              These risk factors were identified based on the current designation mix, vacancy patterns, and unit composition.
            </p>
            <div className="space-y-2">
              {snapshot.concentration_risks.map((risk, i) => (
                <div key={i} className={`flex items-start gap-2 text-xs p-2.5 rounded-lg ${
                  risk.severity === 'critical' ? 'bg-red-50 text-red-700' :
                  risk.severity === 'warning' ? 'bg-orange-50 text-orange-700' :
                  'bg-blue-50 text-blue-700'
                }`}>
                  <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold uppercase text-[10px]">{risk.severity || 'info'}</span>
                    <span className="mx-1">—</span>
                    {risk.detail}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="pt-4 border-t border-gray-200 space-y-1">
          <p className="text-xs text-gray-400 text-center">Generated by Jpro</p>
          <p className="text-[10px] text-gray-400 text-center">
            This report does not constitute legal or tax advice. Consult a qualified tax credit professional for compliance decisions.
          </p>
          {expires_at && (
            <p className="text-[10px] text-gray-400 text-center">
              This shared link expires on {new Date(expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default SharedSnapshot;
